import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, of, forkJoin } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { resultPipe, decisionsPipe, proceedingsPipe, documentsPipe } from './result.pipe';
import { Query } from '../models/query.model';

const CACHE_SIZE = 1;

const context = '/ttab-efoia-api';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  private searchOptionsCache$: Observable<any>;
  private monitorOptionsCache$: Observable<any>;
  private userCache$: Observable<any>;
  private profileCache$: Observable<any>;
  private joblistCache$: Observable<any>;
  private jobstatusCache$: Observable<any>;
  private appConfigCache$: Observable<any>;

  constructor(
    private http: HttpClient
  ) { }

  /**
   * Get the search results from API
   * @param domain the search domain (decisions, proceedings, documents)
   * @param query the query object containing parameters
   */
  public getSearchResults(domain: string, query: Query): Observable<any> {

    const _map = {
      decisions: this.getDecisionResults
    };

    return (_map[domain])
      ? _map[domain](this.http, query)
      : of({
        recordTotalQuantity: 0,
        took: 0,
        results: []
      });

  }

  /**
   * Get decision search results from API
   * @param http the http service
   * @param query the query object
   */
  private getDecisionResults(http: HttpClient, query: any): Observable<any> {
    return http.post<any>(`${context}/decision/search`, query).pipe(resultPipe, decisionsPipe);
  }

  /**
   * Get the proceedings search results
   * @param query the query object
   */
  private getProceedingResults(http: HttpClient, query: any): Observable<any> {
    return http.post<any>(`${context}/proceedings/json`, query).pipe(resultPipe, proceedingsPipe);
  }

  /**
   * Get the documents search results
   * @param http the http service
   * @param query the query object
   */
  private getDocumentResults(http: HttpClient, query: Query): Observable<any> {
    return http.post<any>(`${context}/documents/json`, query).pipe(resultPipe, documentsPipe);
  }

  /**
   * Get the search options from JSON file
   */
  public getSearchOptions(): Observable<any> {
    if (!this.searchOptionsCache$) {
      this.searchOptionsCache$ = this.http.get('assets/data/search-options.json').pipe(
        shareReplay(CACHE_SIZE)
      );
    }
    return this.searchOptionsCache$;
  }

  public getMonitorOptions(): Observable<any> {
    if (!this.monitorOptionsCache$) {
      this.monitorOptionsCache$ = this.http.get('assets/data/monitor-options.json').pipe(
        shareReplay(CACHE_SIZE)
      );
    }
    return this.monitorOptionsCache$;
  }

  /**
   * Get the permissions for user role
   */
  public getProfile(): Observable<any> {
    if (!this.profileCache$) {
      this.profileCache$ = this.http.get('assets/data/profile.json').pipe(
        shareReplay(CACHE_SIZE)
      );
    }
    return this.profileCache$;
  }

  public loadDocument(url: string): Observable<any> {
    return this.http.get(context + url).pipe(documentsPipe);
  }

  public getUserMock(): Observable<any> {
    return this.http.get('assets/data/user.json');
  }

  public getJobHistoryMock(): Observable<any> {
    return this.http.get('assets/data/job-history.json');
  }

  public getJobRunningMock(): Observable<any> {
    return this.http.get('assets/data/job-running.json');
  }

  public getAnomaliesMock(): Observable<any> {
    return this.http.get('assets/data/anomalies.json');
  }

  public getProceedingHistory(query: string): Observable<any[]> {
    return this.http.get<any[]>(`${context}/monitor/cases${query}`);
  }

  public getDocumentHistory(query: string): Observable<any[]> {
    return this.http.get<any[]>(`${context}/monitor/documents${query}`);
  }

  public getJobHistory(query: string): Observable<any[]> {
    return this.http.get<any[]>(`${context}/monitor/jobs${query}`);
  }

  public getJobStatus(query: string): Observable<any[]> {
    return this.http.get<any[]>(`${context}/monitor/jobstatus`);
  }

  public getJobList(): Observable<any[]> {
    if (!this.joblistCache$) {
      this.joblistCache$ = this.http.get<any>(`${context}/monitor/joblist`).pipe(
        shareReplay(CACHE_SIZE)
      );
    }
    return this.joblistCache$.pipe(map(data => data.results));
  }

  public getJobStatusList(): Observable<any[]> {
    if (!this.jobstatusCache$) {
      this.jobstatusCache$ = this.http.get<any>('assets/data/job-status.json').pipe(
        shareReplay(CACHE_SIZE)
      );
    }
    return this.jobstatusCache$.pipe(map(data => data.results));
  }

  public getAnomalies(type: String): Observable<any> {
    return this.http.get<any>(`${context}/monitor/anomalies/${type}`);
  }

  public getAnomaliesOnDemand(type: String): Observable<any> {
    return this.http.get<any>(`${context}/monitor/anomaliesondemand/${type}`);
  }

  public getAnomaliesData(type: String): Observable<any[]> {
    return forkJoin(
      this.getAnomalies(type),
      this.getAnomaliesOnDemand(type)
    ).pipe(
      map(data => {
        const obj: any = data[0].results[0];

        Object.keys(data[1].results[0]).forEach(key => {
          obj[key] = data[1].results[0][key];
        });

        return Object.keys(obj).map(key => {
          return {source: key, count: obj[key]};
        });
      })
    );
  }

  public getAllAnomaliesData(): Observable<any[]> {
    return forkJoin(
      this.getAnomaliesData('PROCEEDING'),
      this.getAnomaliesData('DOCUMENT_PUBLIC'),
      this.getAnomaliesData('DOCUMENT_INTERNAL')
    );
  }

  public getAppConfig() {
    if (!this.appConfigCache$) {
      this.appConfigCache$ = this.http.get<any>('assets/data/app.config.json').pipe(
        shareReplay(CACHE_SIZE)
      );
    }
    return this.appConfigCache$;
  }

}
